import { useState } from "react";
import { Collapse } from "react-bootstrap";
import { IGroupTopStreakRule, ITopStreakHolder } from "../../helpers/managerHelper";
import { AppMessages } from "../../strings";

/**
 * Formats a streak holder name for display.
 */
function formatHolderName(holder: ITopStreakHolder): string {
	const firstName = holder.firstName?.trim() ?? "";
	const lastName = holder.lastName?.trim() ?? "";
	const fullName = `${firstName} ${lastName}`.trim();
	if (fullName) {
		return fullName;
	}
	return AppMessages.topStreakNoHolder;
}

/**
 * Returns CSS grid layout classes for the top-streak tile grid based on rule count.
 */
function getTopStreakTileGridClass(streakCount: number): string {
	if (streakCount === 1) {
		return "top-streak-tile-grid top-streak-tile-grid--single";
	}
	return "top-streak-tile-grid top-streak-tile-grid--multi";
}

/**
 * Renders one longest or current metric column inside a streak rule tile.
 */
const TopStreakMetric = ({
	label,
	holder,
}: {
	label: string;
	holder?: ITopStreakHolder | null;
}) => {
	if (!holder) {
		return (
			<div className="top-streak-metric">
				<div className="top-streak-metric-label">{label}</div>
				<div className="top-streak-metric-days">-</div>
				<div className="top-streak-metric-name top-streak-metric-name--empty">{AppMessages.topStreakNoHolder}</div>
			</div>
		);
	}

	return (
		<div className="top-streak-metric">
			<div className="top-streak-metric-label">{label}</div>
			<div className="top-streak-metric-days">{holder.days}</div>
			<div className="top-streak-metric-name" title={formatHolderName(holder)}>
				{formatHolderName(holder)}
			</div>
		</div>
	);
};

/**
 * Renders one configured streak rule as a dashboard tile.
 * Always shows Longest and Current side-by-side so different holders stay clear.
 */
const TopStreakRuleTile = ({ rule }: { rule: IGroupTopStreakRule }) => {
	return (
		<div className="tile top-streak-tile-card">
			<div className="top-streak-tile-content">
				<div className="top-streak-tile-label fw-semibold" title={rule.name}>
					{rule.name}
				</div>
				<div className="top-streak-metric-row">
					<TopStreakMetric label={AppMessages.streakLongest} holder={rule.longestHolder} />
					<TopStreakMetric label={AppMessages.streakCurrent} holder={rule.currentHolder} />
				</div>
			</div>
		</div>
	);
};

/**
 * Collapsible manager panel listing top current and longest streak holders per configured rule.
 */
const ManagerTopStreaksPanel = ({
	topStreaks,
}: {
	topStreaks: IGroupTopStreakRule[];
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	if (!topStreaks?.length) {
		return null;
	}

	const hasAnyHolder = topStreaks.some((rule) => rule.longestHolder || rule.currentHolder);

	return (
		<div className="row app-page-text">
			<div className="col-12">
				<div className="card" style={{ marginBottom: "0.35rem" }}>
					<div className="card-body top-streaks-panel-body">
						<button
							type="button"
							className="top-streaks-panel-toggle"
							aria-expanded={isExpanded}
							onClick={() => {
								setIsExpanded(!isExpanded);
							}}
						>
							<span className="top-streaks-panel-title">{AppMessages.topStreaksTitle}</span>
							<i className={`fa-solid ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"} top-streaks-panel-chevron`} />
						</button>
						<Collapse in={isExpanded}>
							<div>
								{!hasAnyHolder ? (
									<div className="top-streaks-empty">{AppMessages.topStreaksEmpty}</div>
								) : (
									<div className={getTopStreakTileGridClass(topStreaks.length)}>
										{topStreaks.map((rule) => (
											<TopStreakRuleTile key={rule.streakID} rule={rule} />
										))}
									</div>
								)}
							</div>
						</Collapse>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ManagerTopStreaksPanel;
